import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Grid, MenuItem, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addVisaDemand, editVisaDemand } from '../../../api/endpoints/demands';
import { VISA_APPROVAL_STATUS_LABELS, VISA_TRAVEL_PURPOSES, type VisaDemand } from '../../../api/types/demands';
import { DemandFormDialog } from '../components/DemandFormDialog';
import { PhoneField } from '../../../components/PhoneField';

const schema = z.object({
  employeeID: z.coerce.number().min(1, 'Personel ID gerekli'),
  passengerName: z.string().min(1, 'Ad gerekli'),
  passengerSurname: z.string().min(1, 'Soyad gerekli'),
  passengerMail: z.string().optional(),
  passengerPhone: z.string().optional(),
  passportNo: z.string().min(1, 'Pasaport no gerekli'),
  passportType: z.string().optional(),
  passportIssueDate: z.string().optional(),
  passportExpiryDate: z.string().optional(),
  travelPurpose: z.string().optional(),
  nationality: z.string().optional(),
  dayCount: z.string().optional(),
  country: z.string().min(1, 'Gidilecek ülke gerekli'),
  depDate: z.string().min(1, 'Gidiş tarihi gerekli'),
  retDate: z.string().optional(),
  reason: z.string().optional(),
  isApproved: z.coerce.number().optional(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  employeeID: 0,
  passengerName: '',
  passengerSurname: '',
  passengerMail: '',
  passengerPhone: '',
  passportNo: '',
  passportType: '',
  passportIssueDate: '',
  passportExpiryDate: '',
  travelPurpose: '',
  nationality: 'TR',
  dayCount: '',
  country: '',
  depDate: '',
  retDate: '',
  reason: '',
  isApproved: 0,
};

function toFormValues(demand: VisaDemand): FormValues {
  return {
    employeeID: demand.employeeID ?? 0,
    passengerName: demand.passengerName ?? '',
    passengerSurname: demand.passengerSurname ?? '',
    passengerMail: demand.passengerMail ?? '',
    passengerPhone: demand.passengerPhone ?? '',
    passportNo: demand.passportNo ?? '',
    passportType: demand.passportType ?? '',
    passportIssueDate: demand.passportIssueDate ?? '',
    passportExpiryDate: demand.passportExpiryDate ?? '',
    travelPurpose: demand.travelPurpose ?? '',
    nationality: demand.nationality ?? 'TR',
    dayCount: demand.dayCount ?? '',
    country: demand.country ?? '',
    depDate: demand.depDate ?? '',
    retDate: demand.retDate ?? '',
    reason: demand.reason ?? '',
    isApproved: demand.isApproved ?? 0,
  };
}

/** Create/edit dialog for Vize Talebi. Edit also serves as the status-update action (the
 *  approval-status select, wired to EditVisaDemand's `isApproved` field — see
 *  api/types/demands.ts for the TODO(api-unconfirmed) note on this enum's exact semantics). */
export function VisaDemandDialog({
  open,
  demand,
  onClose,
}: {
  open: boolean;
  demand: VisaDemand | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!demand;

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues,
  });

  useEffect(() => {
    if (open) reset(demand ? toFormValues(demand) : emptyValues);
  }, [open, demand, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && demand?.id ? editVisaDemand({ ...values, id: demand.id }) : addVisaDemand(values),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Talep güncellendi.' : 'Talep oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['demands', 'visa'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <DemandFormDialog
      open={open}
      title={isEdit ? 'Vize Talebini Güncelle' : 'Yeni Vize Talebi'}
      onClose={onClose}
      onSubmit={submit}
      submitting={mutation.isPending}
      error={mutation.error}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Personel ID (EmployeeID)"
            error={!!errors.employeeID}
            helperText={errors.employeeID?.message ?? 'Kurumsal çalışanın sayısal ID değeri'}
            {...register('employeeID')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth size="small" label="Uyruk" {...register('nationality')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Ad"
            error={!!errors.passengerName}
            helperText={errors.passengerName?.message}
            {...register('passengerName')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Soyad"
            error={!!errors.passengerSurname}
            helperText={errors.passengerSurname?.message}
            {...register('passengerSurname')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth size="small" label="E-posta" {...register('passengerMail')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            control={control}
            name="passengerPhone"
            render={({ field }) => (
              <PhoneField value={field.value} onChange={field.onChange} onBlur={field.onBlur} label="Telefon" />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Pasaport No"
            error={!!errors.passportNo}
            helperText={errors.passportNo?.message}
            {...register('passportNo')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth size="small" label="Pasaport Türü" {...register('passportType')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Pasaport Veriliş Tarihi"
            slotProps={{ inputLabel: { shrink: true } }}
            {...register('passportIssueDate')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Pasaport Son Geçerlilik Tarihi"
            slotProps={{ inputLabel: { shrink: true } }}
            {...register('passportExpiryDate')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            control={control}
            name="travelPurpose"
            render={({ field }) => (
              <TextField {...field} select fullWidth size="small" label="Seyahat Amacı">
                <MenuItem value="">—</MenuItem>
                {VISA_TRAVEL_PURPOSES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Gidilecek Ülke (kod)"
            error={!!errors.country}
            helperText={errors.country?.message ?? 'Örn: IT, FR, DE'}
            {...register('country')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Gidiş Tarihi"
            slotProps={{ inputLabel: { shrink: true } }}
            error={!!errors.depDate}
            helperText={errors.depDate?.message}
            {...register('depDate')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Dönüş Tarihi"
            slotProps={{ inputLabel: { shrink: true } }}
            {...register('retDate')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth size="small" label="Gün Sayısı" {...register('dayCount')} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField fullWidth size="small" multiline minRows={2} label="Not / Gerekçe" {...register('reason')} />
        </Grid>
        {isEdit && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="isApproved"
              render={({ field }) => (
                <TextField {...field} select fullWidth size="small" label="Onay Durumu">
                  {Object.entries(VISA_APPROVAL_STATUS_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={Number(value)}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
        )}
      </Grid>
    </DemandFormDialog>
  );
}
